"use client"

import { Area, AreaChart, CartesianGrid, XAxis } from "recharts"
import { DollarSign } from "lucide-react"

import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

const chartConfig = {
  slices: {
    label: "Slices",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig

interface ChartAreaInteractiveProps {
  data: { date: string; slices: number }[];
}

export function ChartAreaInteractive({ data }: ChartAreaInteractiveProps) {
  if (!data) return null;
  const hasData = data.length > 0 && data.some(d => d.slices > 0);

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[250px] text-muted-foreground">
        <DollarSign className="h-12 w-12 mb-3 opacity-40" />
        <p className="font-medium">No earnings data yet</p>
        <p className="text-sm mt-1 text-center">
          Slice donations will appear here as viewers support your content
        </p>
      </div>
    );
  }

  return (
    <ChartContainer
      config={chartConfig}
      className="aspect-auto h-[250px] w-full"
    >
      <AreaChart data={data}>
        <defs>
          <linearGradient id="fillSlices" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor="var(--color-slices)"
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor="var(--color-slices)"
              stopOpacity={0.1}
            />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={(value) => {
            const date = new Date(value)
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            })
          }}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              labelFormatter={(value) => {
                return new Date(value).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })
              }}
              indicator="dot"
            />
          }
        />
        <Area
          dataKey="slices"
          type="natural"
          fill="url(#fillSlices)"
          stroke="var(--color-slices)"
        />
      </AreaChart>
    </ChartContainer>
  )
}
